import React, { useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Typography,
  Paper,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material";
import { baseURL } from "services/API";
import { useAttachLandlord, useBrokerLandlords } from "services/hooks/rentals";
import { useSelector } from "react-redux";
import BrokersLandlordsTable from "components/Tables/BrokersLandlordsTable";
import FormDialog from "components/modal";
import { BrokerLandlordsForm } from "components/Forms/BrokerLandlordForm";

export default function BrokerDetails() {
  const broker = useSelector((state) => state.broker);
  const { brokerLandlords, loadingBrokerLandlords, errorBrokerLandlords, refetchBrokerLandlords } =
    useBrokerLandlords(broker.id);
  const { attachLandlord, loading: attachingLandlord } = useAttachLandlord();

  const [newLandlord, setNewLandlord] = useState("");
  const [brokerCommissionRate, setBrokerCommissionRate] = useState("");
  const [commissionBase, setCommissionBase] = useState("gross");
  const [landlords, setLandlords] = useState([]);
  const [isLoadingLandlords, setLoadingLandlords] = useState(true);
  const [isSubmittingData, setSubmittingData] = useState(false);
  const [open, setOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });

  const handleClose = () => {
    setOpen(false);
    setNewLandlord("");
    setBrokerCommissionRate("");
    setCommissionBase("gross");
  };

  const handleChange = (event) => {
    setNewLandlord(event.target.value);
  };

  const handleOpenAttachLandlord = async () => {
    setOpen(true);
    setLoadingLandlords(true);
    try {
      const response = await axios.get(`${baseURL}/landlords/admin/landlords`);
      setLandlords(response.data.data);
    } catch (e) {
      setSnack({ open: true, message: "Failed to load landlords.", severity: "error" });
    } finally {
      setLoadingLandlords(false);
    }
  };

  const selectedLandlord = landlords.find((l) => l.id === Number(newLandlord));
  const selectedLandlordMaxRate = selectedLandlord?.pay_percentage ?? selectedLandlord?.max_pay_percentage;

  const handleAddLandlord = async () => {
    if (!newLandlord || brokerCommissionRate === "") {
      setSnack({ open: true, message: "Landlord and commission rate are required.", severity: "error" });
      return;
    }
    const rate = Number(brokerCommissionRate);
    if (commissionBase === "net" && rate > 100) {
      setSnack({ open: true, message: "Net commission rate cannot exceed 100%.", severity: "error" });
      return;
    }
    if (commissionBase !== "net" && selectedLandlordMaxRate != null && rate > Number(selectedLandlordMaxRate)) {
      setSnack({
        open: true,
        message: `Gross commission rate cannot exceed landlord pay percentage (${selectedLandlordMaxRate}%).`,
        severity: "error",
      });
      return;
    }

    setSubmittingData(true);
    const response = await attachLandlord(broker.id, newLandlord, rate, commissionBase);
    setSubmittingData(false);

    if (response.success) {
      handleClose();
      refetchBrokerLandlords();
      setSnack({ open: true, message: "Landlord assigned successfully.", severity: "success" });
    } else {
      setSnack({ open: true, message: response.message || "An error occurred.", severity: "error" });
    }
  };

  return (
    <Box m="20px">
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
        <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold", mb: 5, fontSize: 25 }}>
          Broker Details ({broker.broker_name})
        </Typography>
        <Button variant="contained" color="primary" onClick={handleOpenAttachLandlord}>
          Attach New Landlord
        </Button>
      </Stack>
      <Paper sx={{ marginTop: "1rem", overflow: "hidden" }}>
        <BrokersLandlordsTable
          brokerLandlords={brokerLandlords}
          loadingBrokerLandlords={loadingBrokerLandlords}
          errorBrokerLandlords={errorBrokerLandlords}
        />
      </Paper>

      <FormDialog
        open={open}
        title="Assign Landlord"
        text="Attach a landlord to this broker with commission terms"
        content={
          <BrokerLandlordsForm
            newLandlord={newLandlord}
            brokerCommissionRate={brokerCommissionRate}
            commissionBase={commissionBase}
            handleChange={handleChange}
            handleCommissionRateChange={(e) => setBrokerCommissionRate(e.target.value)}
            handleCommissionBaseChange={(e) => setCommissionBase(e.target.value)}
            landlords={landlords}
            isLoadingLandlords={isLoadingLandlords}
            selectedLandlordMaxRate={selectedLandlordMaxRate}
          />
        }
        handleClose={handleClose}
        handleSubmit={handleAddLandlord}
        isSubmittingData={isSubmittingData || attachingLandlord}
      />

      <Snackbar open={snack.open} autoHideDuration={6000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
